import { useState, useMemo, useEffect } from 'react'
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit'
import { GetStaticProps, GetStaticPaths, NextPage } from 'next'
import { useRouter } from 'next/router'
import { defineMessages, useIntl } from 'react-intl'
import SbEditable from 'storyblok-react'
import { Box, Icon, Flex } from '@storyofams/react-ui'
import {
  Seo,
  Container,
  Button,
  IconsSection,
  Header,
  Heading,
  Text,
} from '~components'
import { useShopify } from '~context'
import {
  enhancedStaticProps,
  trackFbAddToCart,
  withLayout,
  gaViewItemEvent,
  gaAddToCartEvent,
  formatWeight,
} from '~lib'
import { DynamicSections } from '~components/dynamic'
import { BottomBar } from '~components/meals'
import { BoxRow, BoxCard, MealsGrid } from '~components/theme-boxes'
import { StaticBoxCard } from '~components/theme-boxes/StaticBoxCard'
import { getShopifySdk } from '~lib/shopify/client'
import { ProductByHandleQuery } from '~lib/shopify/sdk'
import { storyblokSdk } from '~lib/storyblok/client'
import { StaticBoxRow } from '~components/theme-boxes/StaticBoxRow'
import { ThemeBoxHeader } from '~components/theme-boxes/ThemeBoxHeader'
import { ArrowLeft } from '~components/common/Icons'

interface ThemeBoxesPageProps extends WithStoryProps {
  box: ProductByHandleQuery['productByHandle']
  normalSizeMeals?: Array<Object>
  xlSizeMeals?: Array<Object>
}

const messages = defineMessages({
  buyNow: 'Buy now',
  moreInfo: 'More info',
  backToMealboxOverview: 'Back to the overview',
})

const ThemeBoxesPage: NextPage<ThemeBoxesPageProps> = ({
  story,
  box,
  normalSizeMeals,
  xlSizeMeals,
}) => {
  const { pathname, query, replace, push, back: routerBack } = useRouter()
  const intl = useIntl()
  const [variantId, setSelectedVariantId] = useState(query?.variant as string)
  const [staticAmount, setStaticAmount] = useState<number>(1)
  const { cart, updatingCart, addItemToCart, clearCart } = useShopify()

  const updateStaticAmount = newAmount => {
    setStaticAmount(newAmount)
  }

  const selectedVariant = useMemo(
    () =>
      box?.variants?.edges?.find(({ node }) => node.id === variantId)?.node ||
      box?.variants?.edges?.[0]?.node,
    [box, variantId],
  )

  const amount = useMemo(
    () =>
      cart?.lines?.edges.find(item => item.node.merchandise.id === variantId)
        ? cart?.lines?.edges.find(
            item => item.node.merchandise.id === variantId,
          ).node.quantity
        : 0,
    [cart, variantId],
  )

  const updatePath = (id: string) => {
    replace(
      {
        pathname,
        query: {
          handle: query?.handle,
          variant: id,
        },
      },
      undefined,
      { shallow: true },
    )
  }

  useEffect(() => {
    if (
      query?.variant !== variantId &&
      query?.variant !== undefined &&
      variantId !== undefined &&
      !(normalSizeMeals.length && xlSizeMeals.length)
    ) {
      updatePath(variantId)
    }
  }, [variantId])

  useEffect(() => {
    if (
      variantId === undefined &&
      selectedVariant !== undefined &&
      !(normalSizeMeals.length && xlSizeMeals.length)
    ) {
      updatePath(selectedVariant.id)
    }
  }, [])

  useEffect(() => {
    if (!variantId || variantId !== selectedVariant?.id) {
      setSelectedVariantId(selectedVariant?.id)

      if (!!selectedVariant && !!box) {
        gaViewItemEvent({
          currency: selectedVariant.priceV2.currencyCode,
          amount: selectedVariant.priceV2.amount,
          item: {
            item_id: selectedVariant?.sku,
            item_name: box.title,
            item_brand: box.vendor,
            item_variant: selectedVariant.title,
            price: Number(selectedVariant.priceV2.amount),
            quantity: 1,
          },
        })
      }
    }
  }, [selectedVariant])

  const options = useMemo(() => {
    const variants = box?.variants?.edges || null

    if (variants?.length < 2) {
      return null
    }

    return !!variants
      ? variants?.map(({ node }) => ({
          label: `${node.title}`,
          value: node.id,
          availableForSale: node.availableForSale,
        }))
      : []
  }, [box])

  const setSelectedOption = (value: string) => {
    const variantBySelectedOptions = box?.variants?.edges?.find(
      ({ node }) => node?.id === value,
    )?.node

    if (variantBySelectedOptions) {
      const { id } = variantBySelectedOptions

      setSelectedVariantId(id)
    }

    if (!!variantBySelectedOptions && !!box) {
      gaViewItemEvent({
        currency: variantBySelectedOptions.priceV2.currencyCode,
        amount: variantBySelectedOptions.priceV2.amount,
        item: {
          item_id: variantBySelectedOptions?.sku,
          item_name: box.title,
          item_brand: box.vendor,
          item_variant: variantBySelectedOptions.title,
          price: Number(variantBySelectedOptions.priceV2.amount),
          quantity: 1,
        },
      })
    }
  }

  const getBoxContent = () => {
    let boxContent: Array<any> = [
      {
        quantity: 1,
        variantId: selectedVariant?.id,
      },
    ]
    let normalOrXl = 0

    if (options) {
      options.forEach((element, i) => {
        if (element.value === variantId) normalOrXl = i
      })
    }

    if (Array.isArray(normalSizeMeals) && normalOrXl === 0) {
      boxContent = normalSizeMeals.map((item: { quantity: number }) => {
        return {
          ...item,
          quantity: staticAmount ? staticAmount * item.quantity : 1,
          customAttributes: [{ key: 'Mealbox', value: `${box.title}` }],
        }
      })
    }

    if (Array.isArray(xlSizeMeals) && normalOrXl === 1) {
      boxContent = xlSizeMeals.map((item: { quantity: number }) => {
        return {
          ...item,
          quantity: staticAmount ? staticAmount * item.quantity : 1,
          customAttributes: [{ key: 'Mealbox', value: `${box.title}` }],
        }
      })
    }

    return boxContent
  }

  const CheckoutButton = ({ variant }) => {
    const boxContent = getBoxContent()

    return (
      <Button
        variant={!!variant ? variant : 'button_type_2'}
        onClick={async () => {
          const boxVariant = !!box
            ? box.variants.edges.find(el => el.node.id == variantId)
            : null
          const boxItems = boxContent.map(el => {
            return {
              item_id: el?.variantData?.sku,
              item_name: el?.title,
              item_brand: el?.vendor,
              item_variant: el?.variantData?.title,
              price: Number(el?.variantData?.priceV2?.amount),
              quantity: el?.quantity,
            }
          })

          const boxAddItems = boxContent.map(el => ({
            quantity: el.quantity,
            merchandiseId: el.variantId,
          }))

          if (amount < 1 && !boxAddItems.length) {
            addItemToCart(boxAddItems, 'is_box')
          }

          if (boxAddItems.length) {
            addItemToCart(boxAddItems, 'is_box')
          }

          gaAddToCartEvent({
            currency: boxVariant.node.priceV2.currencyCode,
            amount:
              staticAmount < 1
                ? Number(boxVariant.node.priceV2.amount)
                : Number(boxVariant.node.priceV2.amount) * staticAmount,
            items: boxItems,
          })

          //trackFbAddToCart(query?.handle)
          push('/order/overview')
        }}
        minWidth="max-content"
        cursor={updatingCart ? 'wait !important' : 'pointer'}
        disabled={
          updatingCart || (Array.isArray(boxContent) && boxContent.length == 0)
        }
      >
        {intl.formatMessage(messages.buyNow)}
      </Button>
    )
  }

  const OneVariant = ({ textColor }) => {
    return (
      <Flex
        alignItems={'flex-start'}
        width={['100%', '100%', '150px']}
        minWidth={['100%', '100%', '100px']}
        flexDirection={['column']}
        ml={1.5}
      >
        <Text
          width={'fit-content'}
          color={textColor}
          fontSize={['20px']}
          fontWeight={'bold'}
          lineHeight={'1em'}
          style={{
            overflowWrap: 'anywhere',
          }}
        >
          {`(${formatWeight(
            selectedVariant?.weight,
            selectedVariant?.weightUnit,
          )})`}
        </Text>
      </Flex>
    )
  }

  const renderFixed = () => {
    const boxContent = getBoxContent()

    if (!!selectedVariant && boxContent.length)
      return (
        <StaticBoxRow
          amount={amount}
          updateStaticAmount={updateStaticAmount}
          selectedVariant={selectedVariant}
          title={box?.title}
          options={options}
          isOptionDisabled={opt => !opt.availableForSale}
          setSelectedOption={setSelectedOption}
          OneVariant={OneVariant}
        >
          <CheckoutButton variant={'button_type_1'} />
        </StaticBoxRow>
      )
  }

  // const renderStatic = () => {
  //   const boxContent = getBoxContent()

  //   if(!!selectedVariant && boxContent.length)
  //     return (
  //       <StaticBoxCard
  //         amount={amount}
  //         selectedVariant={selectedVariant}
  //         title={box?.title}
  //         updateStaticAmount={updateStaticAmount}
  //         image={box?.images?.edges[0]}
  //         options={options}
  //         isOptionDisabled={(opt) => !opt.availableForSale}
  //         setSelectedOption={setSelectedOption}
  //       >
  //         <CheckoutButton />
  //       </StaticBoxCard>
  //     )
  //   else if (!!selectedVariant)
  //     return (
  //       <BoxCard
  //         amount={amount}
  //         selectedVariant={selectedVariant}
  //         title={box?.title}
  //         image={box?.images?.edges[0]}
  //         options={options}
  //         isOptionDisabled={(opt) => !opt.availableForSale}
  //         setSelectedOption={setSelectedOption}
  //         selectedOptionDisabled={boxContent.length == 0}
  //         useLocalProductQuantity={true}
  //       >
  //         <CheckoutButton />
  //       </BoxCard>
  //     )
  // }
  //console.log('story?.content => ', story?.content)
  return (
    <>
      <Seo
        title={story?.content?.seo?.title || story?.content?.header?.[0]?.text}
        story={story}
      />

      <SbEditable content={story?.content}>
        <Box
          px={0}
          pb={0}
          pt={0}
          mx={0}
          position={'relative'}
          maxWidth={'100%'}
        >
          <Box
            position={['absolute']}
            left={0}
            top={['10px', '10px', '20px']}
            pl={[3, 3, 6]}
            zIndex={10}
          >
            <Button
              variant="unstyled"
              alignItems="center"
              width={'auto'}
              display={'flex'}
              onClick={() => {
                if (window.history.state && window.history.state.idx > 0) {
                  routerBack()
                } else {
                  push('/theme-boxes')
                }
              }}
            >
              <Icon icon={ArrowLeft} fontSize={3} mr={1.5} display={'flex'} />
              {intl.formatMessage(messages.backToMealboxOverview)}
            </Button>
          </Box>
          <ThemeBoxHeader
            fontColor={story?.content?.header_font_color?.color}
            backgroundColor={story?.content?.header_backgroud_color?.color}
            textBlockBackgroundColor={
              story?.content?.header_text_block_background_color?.color
            }
            title={story?.content?.title}
            description={story?.content?.description}
            image={story?.content?.box_image}
            imageMobile={story?.content?.box_image_m}
            useFullHeightImage={story?.content?.image_cover_toggle}
            productSpecs={story?.content?.specs}
            options={options}
            isOptionDisabled={opt => !opt.availableForSale}
            setSelectedOption={setSelectedOption}
            selectedVariant={selectedVariant}
            CheckoutButton={() => <CheckoutButton variant={'button_type_2'} />}
            useBadge={story?.content?.use_badge}
            badgeImage={story?.content?.badge}
            badgePosMX={story?.content?.badge_pos_m_x}
            badgePosMY={story?.content?.badge_pos_m_y}
            badgePosX={story?.content?.badge_pos_x}
            badgePosY={story?.content?.badge_pos_y}
            badgeWidth={story?.content?.badge_width}
            OneVariant={OneVariant}
          />
        </Box>
        {/* <Header
          title={story?.content?.header?.[0]?.text}
          img={story?.content?.header?.[0]?.image}
          textProps={{ justifyContent: 'center' }}
          alignItems="center"
        /> */}

        <SbEditable content={story?.content}>
          {/* {story?.content?.box_advantages?.[0] && (
            <IconsSection {...story?.content?.box_advantages?.[0]} mb={15} />
          )} */}
          <DynamicSections
            content={{ sections: story?.content?.dynamic_sections }}
            disableFirst
          />

          <MealsGrid
            title={story?.content?.mealsInBoxTitle}
            meals={story?.content?.mealsInBox}
            backgroundColor={story?.content?.backgroud_color?.color}
            moreInfoButtonText={intl.formatMessage(messages.moreInfo)}
          />

          <DynamicSections
            mt={['64px !important', '128px !important']}
            content={{ sections: story?.content?.dynamic_sections2 }}
            disableFirst
          />
        </SbEditable>
        <BottomBar noDivider renderFixed={renderFixed} />
      </SbEditable>
    </>
  )
}

export default withLayout(withStory(ThemeBoxesPage))

export const getStaticProps: GetStaticProps<ThemeBoxesPageProps> =
  enhancedStaticProps(async ({ sdk, params, defaultLocale, locale }) => {
    let story = null
    let productByHandle = null
    let notFound = false
    let normalSizeMeals = []
    let xlSizeMeals = []
    const mealsInBoxQty = []
    const backupMeals = {
      normal: [],
      xl: [],
    }

    const getMealQty = foodNodeTitle => {
      let returnQty = 1

      if (mealsInBoxQty) {
        mealsInBoxQty.forEach(mealInBox => {
          if (mealInBox.mealTitle === foodNodeTitle.toLowerCase())
            returnQty = Number(mealInBox.mealQty)
        })
      }

      return returnQty
    }

    const checkIfPrimaryMeal = (productNode, variantNode, backupArr) => {
      const mealIsInPrimary = Boolean(
        mealsInBoxQty.filter(
          mealInBox => mealInBox.mealTitle === productNode.title.toLowerCase(),
        ).length,
      )

      if (!mealIsInPrimary) {
        if (variantNode.availableForSale && !backupArr.includes(variantNode.id))
          backupArr.push({
            variantId: variantNode.id,
            title: productNode.title,
            vendor: productNode.vendor,
            variantData: variantNode,
          })

        return false
      }

      return true
    }

    try {
      const id = `theme-boxes/${params.handle}`
      if (defaultLocale !== locale) {
        story = (await sdk.themeboxpageItemEnglish({ id: `${locale}/${id}` }))
          .ThemeboxpageItem
      } else {
        story = (await sdk.themeboxpageItem({ id })).ThemeboxpageItem
      }
    } catch (e) {
      notFound = true
    }

    if (story) {
      try {
        productByHandle = (
          await getShopifySdk(locale).productByHandle({
            handle: `${story.content.shopifyProduct.item.blob.handle}`,
          })
        ).productByHandle

        if (story.content.mealBoxCollectionHandle) {
          /* Quantity query */
          if (story.content.mealsInBox) {
            story.content.mealsInBox.map(mealInBox => {
              const updatedTitle = mealInBox.content.title
                .split(' ')
                .filter(str => !(str.includes('(') && str.includes('x)')))
                .join(' ')
                .trim()

              mealsInBoxQty.push({
                mealTitle: updatedTitle.toLowerCase(),
                mealQty: mealInBox.content.quantity
                  ? Number(mealInBox.content.quantity)
                  : 1,
              })
            })
          }

          /* Normal size query */
          await getShopifySdk()
            .productInCollectionByHandle({
              handle: story.content.mealBoxCollectionHandle,
              qty: 16,
            })
            .then(res => {
              const normalSizeMealsTempArr = []
              const xlSizeMealsTempArr = []

              if (
                res.collectionByHandle &&
                res.collectionByHandle.products.edges.length
              ) {
                res['collectionByHandle']['products']['edges'].map(food => {
                  const normalPortionVariant =
                    food &&
                    food.node.variants.edges.find(variant => {
                      return !!variant?.node?.selectedOptions.find(
                        el => el.name === 'Portion' && el.value === 'Normal',
                      )
                    })

                  if (
                    normalPortionVariant?.node?.availableForSale &&
                    checkIfPrimaryMeal(
                      food.node,
                      normalPortionVariant.node,
                      backupMeals.normal,
                    )
                  ) {
                    normalSizeMealsTempArr.push({
                      quantity: getMealQty(food.node.title),
                      variantId: normalPortionVariant.node.id,
                      title: food.node.title,
                      vendor: food.node.vendor,
                      variantData: normalPortionVariant.node,
                    })
                  }
                })
              }

              if (
                res.collectionByHandle &&
                res.collectionByHandle.products.edges.length
              ) {
                res['collectionByHandle']['products']['edges'].map(food => {
                  const xlPortionVariant =
                    food &&
                    food.node.variants.edges.find(variant => {
                      return !!variant?.node?.selectedOptions.find(
                        el => el.name === 'Portion' && el.value === 'XL',
                      )
                    })

                  if (
                    xlPortionVariant?.node?.availableForSale &&
                    checkIfPrimaryMeal(
                      food.node,
                      xlPortionVariant.node,
                      backupMeals.xl,
                    )
                  ) {
                    xlSizeMealsTempArr.push({
                      quantity: getMealQty(food.node.title),
                      variantId: xlPortionVariant.node.id,
                      title: food.node.title,
                      vendor: food.node.vendor,
                      variantData: xlPortionVariant.node,
                    })
                  }
                })
              }

              normalSizeMeals = normalSizeMealsTempArr
              xlSizeMeals = xlSizeMealsTempArr
            })

          const currentNormalBoxQuantity = () =>
            normalSizeMeals.reduce((acc, e) => e.quantity + acc, 0)
          const currentXLBoxQuantity = () =>
            xlSizeMeals.reduce((acc, e) => e.quantity + acc, 0)

          /* Check if current box contains at least 8 products (Normal) */
          if (currentNormalBoxQuantity() < 8 && backupMeals.normal.length > 0) {
            normalSizeMeals.push({
              variantId: backupMeals.normal[0].variantId,
              title: backupMeals.normal[0].title,
              vendor: backupMeals.normal[0].vendor,
              variantData: backupMeals.normal[0].variantData,
              quantity: 8 - currentNormalBoxQuantity(),
            })
          }

          /* Check if current box contains at least 8 products (XL) */
          if (currentXLBoxQuantity() < 8 && backupMeals.xl.length > 0) {
            xlSizeMeals.push({
              variantId: backupMeals.xl[0].variantId,
              title: backupMeals.xl[0].title,
              vendor: backupMeals.xl[0].vendor,
              variantData: backupMeals.xl[0].variantData,
              quantity: 8 - currentXLBoxQuantity(),
            })
          }

          /* If no backup meal available, revert back to normal logic */
          if (currentNormalBoxQuantity() < 8)
            while (normalSizeMeals.length) normalSizeMeals.pop()

          if (currentXLBoxQuantity() < 8)
            while (xlSizeMeals.length) xlSizeMeals.pop()
        }
      } catch (e) {
        console.log(e)
        notFound = true
      }
    }

    if (notFound || !story) {
      return {
        props: {
          story: {},
          box: {},
        },
        notFound: true,
        revalidate:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
      }
    }

    return {
      props: {
        story,
        box: productByHandle,
        normalSizeMeals: normalSizeMeals,
        xlSizeMeals: xlSizeMeals,
      },
      notFound: notFound || !story,
      revalidate:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
    }
  })

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pages = (await storyblokSdk.themeboxpageItems()).ThemeboxpageItems

  return {
    paths: pages?.items
      ?.map(({ slug }) => ({
        handle: slug,
      }))
      ?.map(params => locales.map(locale => ({ params, locale })))
      ?.flat(),
    fallback: 'blocking',
  }
}
